'use client';

import { useTranslations } from 'next-intl';
import { type KeyboardEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useClickOutside } from '@/app/hooks';
import { PAUSE_PRESETS, TTS_LIMITS } from '../constant';
import type { ActivePauseTag } from '../types';

interface PausePopoverProps {
  pauseTag: ActivePauseTag;
  onChange: (duration: number) => void;
  onClose: () => void;
}

export function PausePopover({ pauseTag, onChange, onClose }: PausePopoverProps) {
  const t = useTranslations();
  const popoverRef = useRef<HTMLDivElement>(null);
  const isEditing = useRef(false);
  const positionRef = useRef<{ top: number; left: number }>({ top: 0, left: 0 });

  const isPreset = PAUSE_PRESETS.includes(pauseTag.duration as (typeof PAUSE_PRESETS)[number]);
  const [customValue, setCustomValue] = useState(isPreset ? '' : String(pauseTag.duration));

  useEffect(() => {
    setCustomValue(isPreset ? '' : String(pauseTag.duration));
  }, [isPreset, pauseTag.duration]);

  useLayoutEffect(() => {
    if (!pauseTag.element) return;
    const rect = pauseTag.element.getBoundingClientRect();
    positionRef.current = {
      top: rect.bottom + 6,
      left: rect.left,
    };
    if (popoverRef.current) {
      popoverRef.current.style.top = `${positionRef.current.top}px`;
      popoverRef.current.style.left = `${positionRef.current.left}px`;
    }
  }, [pauseTag.element]);

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (isEditing.current) return;
      const target = event.target as Node;
      if (!pauseTag.element.contains(target)) {
        onClose();
      }
    },
    [onClose, pauseTag.element],
  );

  useClickOutside(popoverRef, handleClickOutside);

  const handlePresetClick = (duration: number) => {
    onChange(duration);
    onClose();
  };

  const clampDuration = (val: number): number => {
    return Math.min(TTS_LIMITS.MAX_PAUSE, Math.max(TTS_LIMITS.MIN_PAUSE, val));
  };

  const handleCustomSubmit = () => {
    const num = Number.parseFloat(customValue);
    if (Number.isNaN(num)) return;
    const clamped = clampDuration(num);
    setCustomValue(String(clamped));
    onChange(clamped);
    onClose();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleCustomSubmit();
    }
  };

  return (
    <div
      ref={popoverRef}
      className="fixed z-50 flex items-center gap-2 rounded-8 bg-system-bg02 p-2 shadow-lg"
      style={{ top: positionRef.current.top, left: positionRef.current.left }}
    >
      {PAUSE_PRESETS.map((preset) => (
        <button
          key={preset}
          type="button"
          className={`flex h-8 items-center justify-center rounded-8 px-3 text-sm transition-colors ${
            pauseTag.duration === preset
              ? 'bg-system-white24 text-system-white'
              : 'bg-system-white08 text-system-white/60 hover:bg-system-white16 hover:text-system-white'
          }`}
          onClick={() => handlePresetClick(preset)}
        >
          {preset}s
        </button>
      ))}
      <div className="flex items-center gap-1">
        <input
          type="number"
          placeholder={t('tools.textToSpeech.pauseCustomPlaceholder')}
          value={customValue}
          min={TTS_LIMITS.MIN_PAUSE}
          max={TTS_LIMITS.MAX_PAUSE}
          step={0.01}
          onChange={(e) => setCustomValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            isEditing.current = true;
          }}
          onBlur={() => {
            isEditing.current = false;
          }}
          className="flex h-8 w-16 items-center rounded-md bg-system-white/[.12] px-2 text-center text-sm text-system-white leading-[1.4] outline-none backdrop-blur-[30px]"
        />
      </div>
    </div>
  );
}
